import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BaseDaoService, handleError} from '@q9elements/ui-kit/common';
import {Observable, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';

import {
  IColumnField,
  ICustomViewBoby
} from '../../editor-interface/components/custom-metadata-view/dialogs/models/create-new-view.interface';
import {
  ICustomView,
  ICustomViewFilter,
  ICustomViewSort,
  ICustomViewUpdate
} from '../../editor-interface/models/custom-view.interface';
import {ISearchQuery} from '../../editor-interface/models/grid-query.interface';
import {ICustomViewStrategyResult} from '../../editor-interface/models/grid-strategy-result.interface';
import {IMetadataList} from '../../editor-interface/models/metadata-list.interface';
import {CustomViewService} from '../../editor-interface/services/custom-view.service';

@Injectable({
  providedIn: 'root'
})
export class CustomViewDaoService extends BaseDaoService {
  constructor(private customViewService: CustomViewService) {
    super();
  }

  public getCustomViews(refModelId: string): Observable<ICustomView[]> {
    const url = `${this.apiUrl}/custom-views/${refModelId}`;

    return this.http.get<ICustomView[]>(url);
  }

  public getCustomView(refModelId: string, viewId: string): Observable<ICustomView> {
    const url = `${this.apiUrl}/custom-views/${refModelId}/view/${viewId}`;

    return this.http.get<ICustomView>(url).pipe(
      catchError(err => {
        this.getCustomViews(refModelId).subscribe(customViews => {
          this.customViewService.storeCustomViewList(customViews);
        });
        return throwError(err);
      })
    );
  }

  public updateCustomView(
    refModelId: string,
    viewId: string,
    params: Partial<ICustomViewUpdate>
  ): Observable<ICustomView> {
    const url = `${this.apiUrl}/custom-views/${refModelId}/view/${viewId}`;

    return this.http.put<ICustomView>(url, params);
  }

  public getCustomViewMetadataList(refModelId: string): Observable<IMetadataList> {
    const url = `${this.apiUrl}/custom-views/${refModelId}/metadata-list`;

    return this.http.get<IMetadataList>(url);
  }

  public getAvailableColumnsList(refModelId: string, params): Observable<IColumnField[]> {
    const url = `${this.apiUrl}/custom-views/${refModelId}/columns`;

    return this.http.post<IColumnField[]>(url, params);
  }

  public createCustomView(refModelId: string, params: ICustomViewBoby): Observable<ICustomView> {
    const url = `${this.apiUrl}/custom-views/${refModelId}`;

    return this.http.post<ICustomView>(url, params).pipe(
      tap((customView: ICustomView) => {
        this.customViewService.selectedView = customView;
      })
    );
  }

  public removeCustomView(refModelId: string, viewId: string): Observable<ICustomView> {
    const url = `${this.apiUrl}/custom-views/${refModelId}/view/${viewId}`;

    return this.http.delete<ICustomView>(url);
  }

  public getCustomViewNodes(
    refModelId: string,
    viewId: string,
    queryParams: ISearchQuery
  ): Observable<ICustomViewStrategyResult> {
    const {skip, limit} = queryParams;
    const query = new HttpParams().set('skip', skip.toString()).set('limit', limit.toString());
    const url = `${this.apiUrl}/refmodel/${refModelId}/custom-view/${viewId}`;

    return this.http.get<ICustomViewStrategyResult>(url, {params: query});
  }

  public getQuickCleanUpView(refModelId: string, qcView: string) {
    const url = `${this.apiUrl}/qc-views/${refModelId}/${qcView}`;

    return this.http.get<ICustomViewStrategyResult>(url).pipe(handleError());
  }

  public getQCVWithSettings(
    refModelId: string,
    qcView: string,
    filters: ICustomViewFilter[],
    sort: ICustomViewSort | null
  ): Observable<ICustomView> {
    const url = `${this.apiUrl}/qc-views/settings/${refModelId}/${qcView}`;

    return this.http.post<ICustomView>(url, {filters, sort}).pipe(handleError());
  }

  public updateFilters(
    refModelId: string,
    viewId: string,
    params: ICustomViewFilter[]
  ): Observable<ICustomView> {
    const url = `${this.apiUrl}/custom-views/${refModelId}/view/${viewId}/filters`;

    return this.http.put<ICustomView>(url, params);
  }

  public updateSort(
    refModelId: string,
    viewId: string,
    params: {sort: ICustomViewSort | null}
  ): Observable<ICustomView> {
    const url = `${this.apiUrl}/custom-views/${refModelId}/view/${viewId}/sort`;

    return this.http.put<ICustomView>(url, params);
  }

  public downloadCustomViewCsv(refModelId: string, customViewId: string) {
    const url = `${this.apiUrl}/download/custom-views/${refModelId}/view/${customViewId}/csv`;

    return this.http.post(url, {timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone});
  }
}
